// Voicemail and routing (contract section 16): tenant defaults, shared-number greetings, each
// rep's routing status and tenant-wide blocked numbers. Managers read, admins change.
import { queryOptions } from "@tanstack/react-query";
import { api } from "./client";

export type ForwardCallerId = "caller" | "number";
export type ForwardMode = "off" | "always" | "no_answer" | "offline";
export type GreetingSlot = "standard" | "after_hours";

export interface VoicemailSettings {
  ring_secs: number;
  max_secs: number;
  greeting_text: string | null;
  forwarding_enabled: boolean;
  forward_caller_id: ForwardCallerId;
  /** Forwarded minutes a rep may use per day; past it calls go to voicemail. */
  forward_daily_minutes: number;
  /** IANA zone for reps who set none. */
  time_zone: string;
}

export interface VoicemailSettingsView {
  settings: VoicemailSettings;
  /** The built-in default greeting template. */
  default_greeting: string;
}

export interface Greeting {
  slot: GreetingSlot;
  kind: "audio" | "tts";
  text?: string;
  voice?: string;
  duration_ms?: number;
  updated_at: string;
}

export interface Voice {
  id: string;
  label: string;
}

export interface GreetingSet {
  standard?: Greeting;
  after_hours?: Greeting;
  default_text: string;
  voices: Voice[];
}

export interface NumberGreetings {
  number_id: string;
  e164: string;
  label: string;
  members: number;
  greetings: GreetingSet;
}

export type StatusNow =
  | { kind: "available" }
  | { kind: "dnd"; until?: string | null }
  | { kind: "after_hours" }
  | { kind: "forwarding"; to: string };

export interface RepRouting {
  user: { id: string; name: string; email?: string };
  status: StatusNow;
  forward_mode: ForwardMode;
  forward_to?: string;
  hours_enabled: boolean;
  time_zone?: string;
  custom_greeting: boolean;
  /** Forwarded minutes today (the company's zone). */
  forward_minutes_today: number;
  /** Today's forwarding limit is used up. */
  forward_cap_reached: boolean;
  updated_at: string | null;
}

export interface BlockedNumber {
  e164: string;
  scope: "mine" | "everyone";
  contact_name?: string;
  created_at: string;
  created_by?: { id: string; name: string };
}

export interface AudioUrl {
  url: string;
  expires_at: string;
}

const greetingPath = (id: string, slot: GreetingSlot) =>
  `/v1/admin/voicemail/numbers/${encodeURIComponent(id)}/greetings/${slot}`;

export const voicemail = {
  settings: () => api<VoicemailSettingsView>("/v1/admin/voicemail"),
  putSettings: (s: VoicemailSettings) =>
    api<VoicemailSettingsView>("/v1/admin/voicemail", { method: "PUT", body: s }),
  reps: () => api<{ items: RepRouting[] }>("/v1/admin/voicemail/reps"),
  numbers: () => api<{ items: NumberGreetings[] }>("/v1/admin/voicemail/numbers"),
  putTts: (id: string, slot: GreetingSlot, text: string, voice: string) =>
    api<Greeting>(greetingPath(id, slot), { method: "PUT", body: { text, voice } }),
  putAudio: (id: string, slot: GreetingSlot, file: Blob, type: string) =>
    api<Greeting>(`${greetingPath(id, slot)}/audio`, { method: "PUT", raw: { body: file, type } }),
  deleteGreeting: (id: string, slot: GreetingSlot) => api<void>(greetingPath(id, slot), { method: "DELETE" }),
  greetingAudio: (id: string, slot: GreetingSlot) =>
    api<AudioUrl>(`${greetingPath(id, slot)}/audio`, { query: { format: "json" } }),
  blocked: () => api<{ items: BlockedNumber[] }>("/v1/admin/blocked"),
  block: (e164: string) => api<BlockedNumber>("/v1/admin/blocked", { method: "POST", body: { e164 } }),
  unblock: (e164: string) => api<void>(`/v1/admin/blocked/${encodeURIComponent(e164)}`, { method: "DELETE" }),
};

export const vmKeys = {
  settings: ["admin", "voicemail", "settings"] as const,
  reps: ["admin", "voicemail", "reps"] as const,
  numbers: ["admin", "voicemail", "numbers"] as const,
  blocked: ["admin", "voicemail", "blocked"] as const,
};

export const vmSettingsQuery = queryOptions({ queryKey: vmKeys.settings, queryFn: voicemail.settings });
export const vmRepsQuery = queryOptions({ queryKey: vmKeys.reps, queryFn: voicemail.reps });
export const vmNumbersQuery = queryOptions({ queryKey: vmKeys.numbers, queryFn: voicemail.numbers });
export const vmBlockedQuery = queryOptions({ queryKey: vmKeys.blocked, queryFn: voicemail.blocked });

/** The audio content type for an upload, from the file's type or its name. */
export function audioType(file: File): string | null {
  if (file.type === "audio/mpeg" || file.type === "audio/mp3") return "audio/mpeg";
  if (file.type === "audio/wav" || file.type === "audio/x-wav" || file.type === "audio/wave")
    return "audio/wav";
  const name = file.name.toLowerCase();
  if (name.endsWith(".mp3")) return "audio/mpeg";
  if (name.endsWith(".wav")) return "audio/wav";
  return null;
}

/** Normalizes a typed US number to E.164, or null. */
export function toE164(raw: string): string | null {
  const t = raw.trim();
  const digits = t.replace(/\D/g, "");
  if (t.startsWith("+")) return /^\+[1-9]\d{7,14}$/.test(`+${digits}`) ? `+${digits}` : null;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

export const RING_MIN = 10;
export const RING_MAX = 60;
